import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { map, take } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { RestAPIService } from 'app/services/rest-api.service';
import { GetHistorizedInterface, TagDetail } from 'app/services/graphqlApi.service';
import { LoginService } from './../../auth/services/login.service';
import { DataService } from '../data/data.service';
import { getHistorizedData, getTags, getTagsByName, getTagTypes } from '../constants/gql';
import { TagType } from './../tags/interfaces/tag-type';
import { Tag } from '../tags/interfaces/tag';

@Injectable({
  providedIn: 'root'
})
export class TagsService {

  private _tagTypeSource = new BehaviorSubject<TagType[]>([]);
  tagTypes$ = this._tagTypeSource.asObservable();
  private _entityTags = new BehaviorSubject<Tag[]>([]);
  entityTags$ = this._entityTags.asObservable();

  private _hidtorizedTypes = new BehaviorSubject([]);
  historizedTypes$ = this._hidtorizedTypes.asObservable();

  constructor(
    private apiService: RestAPIService,
    private loginService: LoginService,
    private readonly dataService: DataService,
    private toastr: ToastrService) {
      this.loginService.login$.subscribe(isLoggedIn => {
        if(isLoggedIn) {
          this.getTagTypes();
        }
      });
    }

  getEntityTags(entity_name: string) {
    return this.apiService.getGQLData({ query: getTagsByName.query, params: { entityName: entity_name } }).pipe(map(tag => {
      return tag?.data?.intellija_tags?.map(d => {
        const states = (d.state_tags || []).map(s =>
          ({
            color: s.color,
            value: s.value,
            isBlink: s.is_blink,
            id: s.id,
            state: s.state
          }));

        return {
          id: d.id,
          allowWrite: d.allow_write || false,
          entity_id: d.entity_id,
          name: d.name,
          tagTypeId: d.tag_type_id,
          tagType: d.ref_tag_type?.name,
          displayName: d.display_name,
          description: d.description,
          alarms: null,
          action: null,
          historizedId: d.historized_id,
          displayEuId: d.display_eu_id,
          factor: d.factor,
          offset: d.offset,
          precision: d.precision,
          rejectMax: d.reject_max,
          rejectMin: d.reject_min,
          histPercentChange: d.hist_percent_change,
          isThousandSeparator: d.is_thousand_separator || false,
          measurementCategory: d.measurement_category,
          states
        };
      });
    }));
  }

  getEntityTagsByID(entity_id: string) {
    return this.apiService.getGQLData({ query: getTags.query, params: { entityId: entity_id } }).pipe(map(tag => {
      return tag?.data?.intellija_tags?.map(d => {
        const states = (d.state_tags || []).map(s =>
          ({
            color: s.color,
            value: s.value,
            isBlink: s.is_blink,
            id: s.id,
            state: s.state
          }));

        return {
          id: d.id,
          allowWrite: d.allow_write || false,
          entity_id: d.entity_id,
          name: d.name,
          tagTypeId: d.tag_type_id,
          tagType: d.ref_tag_type?.name,
          displayName: d.display_name,
          description: d.description,
          alarms: null,
          action: null,
          historizedId: d.historized_id,
          displayEuId: d.display_eu_id,
          calc_tag_config: d.calc_tag_config || null,
          factor: d.factor,
          offset: d.offset,
          precision: d.precision,
          rejectMax: d.reject_max,
          rejectMin: d.reject_min,
          histPercentChange: d.hist_percent_change,
          isThousandSeparator: d.is_thousand_separator || false,
          measurementCategory: d.measurement_category,
          states,
          alarmsCount: d.alarms_configs?.length || 0,
        };
      });
    }));
  }

  setEntityTagsById(id: string) {
    this.getEntityTagsByID(id).pipe(take(1)).subscribe(tags => {
      this._entityTags.next(tags);
    });
  }

  deleteTag(entityType: string, entityUUID: string, type: string, uuid: string) {
    return this.apiService.delete(`${entityType}/${entityUUID}/${type}/${uuid}`)
    .toPromise().then(res => {
      const filteredTags: Tag[] = this._entityTags.getValue().filter(tag => tag.id !== uuid);
      this._entityTags.next(filteredTags);
      const tags: TagDetail[] = this.dataService.tags.getValue().filter((tag) => tag.id !== uuid);
      this.dataService.tags.next(tags);
      // if (res.result.status === 'ok') {
        this.toastr.success('Tag deleted successfully!');
        return Promise.resolve();
      // } else {
      //   this.toastr.error(res.error.message);
      //   return Promise.reject(res.error);
      // }
    }, err => {
      this.toastr.error('An error has occured.', err?.error?.error?.message);
      return Promise.reject(err.error);
    });
  }

  getTagTypes() {
    // const tagSources = this._tagTypeSource.getValue();
    // if(tagSources?.length > 0) return;

    this.apiService.getGQLData({ query: getTagTypes.query}).pipe(map(tag => {
      return tag.data?.intellija_ref_tag_types?.map((d: TagType) => {
        return {
          name: d.name,
          id: d.id,
          is_active: d.is_active
        };
      });
    })).subscribe(res => {
      this._tagTypeSource.next(res);
    });
  }

  getHistirized() {
    return this.apiService.getGQLData({ query: getHistorizedData.query }).pipe(
        map((response) => {
            const queryResponse: GetHistorizedInterface[] = response.data?.intellija_ref_historized;
            return queryResponse.map(h => ({id: h.id, name: h.name})) || [];
        }));
  }

  setHistorized() {
    const historized = this._hidtorizedTypes.getValue();
    if(historized?.length > 0) return;

    this.getHistirized().subscribe(data => {
      this._hidtorizedTypes.next(data);
    });
  }

}
